import mqtt from 'mqtt';

let client;

const pendingCommands = new Map();
const subscribedDevices = new Set();

const ACK_TIMEOUT = 4000;

export const getMqttClient = () => {
  if (!client) {
    client = mqtt.connect(
      'wss://robopower.xyz:8084/mqtt',
      {
        clientId: `react_web_${Math.random()
          .toString(16)
          .substring(2, 10)}`,

        username: 'alcobrake-backend',
        password: 'alcobrakeusa',

        clean: true,
        reconnectPeriod: 30000,
        connectTimeout: 10000,
        keepalive: 60,
      }
    );

    client.on('connect', () => {
      console.log('✅ MQTT connected');

      // Re-subscribe after MQTT reconnect
      subscribedDevices.forEach(
        deviceId => {
          const topic =
            `device/${deviceId}/cmdack`;

          client.subscribe(
            topic,
            { qos: 1 },
            error => {
              if (error) {
                console.error(
                  '❌ MQTT re-subscribe failed:',
                  topic,
                  error.message,
                );
              } else {
                console.log(
                  '✅ MQTT re-subscribed:',
                  topic,
                );
              }
            },
          );
        }
      );
    });

    client.on(
      'message',
      (topic, payload) => {
        console.log(
          '📥 MQTT:',
          topic,
          payload.toString(),
        );

        /*
         * Extract CBID from:
         *
         * device/ABC_xxx/cmdack
         */
        const match = topic.match(
          /^device\/(.+)\/cmdack$/,
        );

        if (!match) {
          console.log(
            '⚠️ Ignoring non-cmdack topic:',
            topic,
          );

          return;
        }

        const ackDeviceId =
          match[1];

        let data;

        try {
          data = JSON.parse(
            payload.toString(),
          );
        } catch (error) {
          console.log(
            '❌ Invalid MQTT JSON',
          );

          return;
        }

        if (!data?.id) {
          console.log(
            '⚠️ CMDACK has no id',
          );

          return;
        }

        const pending =
          pendingCommands.get(
            data.id,
          );

        /*
         * UUID did not match anything
         * we are currently waiting for.
         */
        if (!pending) {
          console.log(
            '⚠️ ACK UUID does not match pending command:',
            data.id,
          );

          return;
        }

        /*
         * Also make sure ACK came from
         * the correct CBID topic.
         */
        if (
          pending.deviceId !==
          ackDeviceId
        ) {
          console.log(
            '❌ ACK device mismatch',
          );

          console.log(
            'Expected:',
            pending.deviceId,
          );

          console.log(
            'Received:',
            ackDeviceId,
          );

          return;
        }

        console.log(
          '✅ SAME UUID RECEIVED:',
          data.id,
        );

        console.log(
          '✅ ACK DEVICE MATCHED:',
          ackDeviceId,
        );

        clearTimeout(
          pending.timer,
        );

        pendingCommands.delete(
          data.id,
        );

        pending.resolve({
          success: true,
          method: 'mqtt',
          message:
            pending.message,
          ack: data,
        });
      }
    );

    client.on(
      'error',
      error => {
        console.error(
          'MQTT connection error:',
          error.message,
        );
      }
    );

    client.on(
      'offline',
      () => {
        console.log(
          '⚠️ MQTT OFFLINE',
        );
      }
    );

    client.on(
      'close',
      () => {
        console.log(
          '⚠️ MQTT CLOSED',
        );
      }
    );
  }

  return client;
};


export const waitForMqttConnection =
  signal =>
    new Promise(
      (resolve, reject) => {
        const connection =
          getMqttClient();

        let timer;

        const cleanup = () => {
          clearTimeout(timer);

          connection.removeListener(
            'connect',
            onConnect,
          );

          signal?.removeEventListener(
            'abort',
            onAbort,
          );
        };

        const onConnect = () => {
          cleanup();
          resolve();
        };

        const onAbort = () => {
          cleanup();

          reject(
            new Error(
              'Connection check cancelled',
            )
          );
        };

        if (signal?.aborted) {
          onAbort();
          return;
        }

        if (
          connection.connected
        ) {
          resolve();
          return;
        }

        connection.on(
          'connect',
          onConnect,
        );

        signal?.addEventListener(
          'abort',
          onAbort,
          {
            once: true,
          }
        );

        timer = setTimeout(
          () => {
            cleanup();

            reject(
              new Error(
                'MQTT connection timed out after 30 seconds',
              )
            );
          },
          30000
        );
      }
    );


export const subscribeToDevice =
  deviceId =>
    new Promise(
      (resolve, reject) => {
        const connection =
          getMqttClient();

        const target =
          String(
            deviceId || '',
          ).trim();

        if (!target) {
          reject(
            new Error(
              'Device ID required',
            )
          );

          return;
        }

        /*
         * Already subscribed.
         */
        if (
          subscribedDevices.has(
            target,
          )
        ) {
          console.log(
            'ℹ️ Already subscribed:',
            `device/${target}/cmdack`,
          );

          resolve();
          return;
        }

        const topic =
          `device/${target}/cmdack`;

        connection.subscribe(
          topic,
          {
            qos: 1,
          },
          error => {
            if (error) {
              console.error(
                '❌ Subscribe failed:',
                topic,
                error.message,
              );

              reject(error);

              return;
            }

            subscribedDevices.add(
              target,
            );

            console.log(
              '✅ Subscribed:',
              topic,
            );

            resolve();
          },
        );
      }
    );

export const unsubscribeFromDevice = deviceId =>
  new Promise((resolve, reject) => {
    const connection = getMqttClient();

    const target = String(
      deviceId || ''
    ).trim();

    if (!target) {
      resolve();
      return;
    }

    const topic =
      `device/${target}/cmdack`;

    // Remove first so reconnect will NOT subscribe again
    subscribedDevices.delete(target);

    // Clear pending commands for this device
    pendingCommands.forEach(
      (pending, id) => {
        if (
          pending.deviceId === target
        ) {
          clearTimeout(
            pending.timer
          );

          pendingCommands.delete(
            id
          );

          pending.reject?.(
            new Error(
              'DEVICE_UNSUBSCRIBED'
            )
          );
        }
      }
    );

    if (!connection.connected) {
      console.log(
        'ℹ️ MQTT already disconnected. Removed subscription state:',
        topic
      );

      resolve();
      return;
    }

    connection.unsubscribe(
      topic,
      error => {
        if (error) {
          console.error(
            '❌ MQTT unsubscribe failed:',
            topic,
            error.message
          );

          reject(error);
          return;
        }

        console.log(
          '✅ Unsubscribed:',
          topic
        );

        resolve();
      }
    );
  });
export const sendCommand = async (
  deviceId,
  cmd,
  resp = '',
) => {
  const connection =
    getMqttClient();

  const target =
    String(
      deviceId || '',
    ).trim();

  if (
    !target ||
    /[\s/+#]/.test(target)
  ) {
    throw new Error(
      'A valid device ID is required',
    );
  }

  if (
    cmd === undefined ||
    cmd === null ||
    String(cmd).trim() === ''
  ) {
    throw new Error(
      'Command is required',
    );
  }

  if (
    !connection.connected
  ) {
    throw new Error(
      'MQTT_NOT_CONNECTED',
    );
  }

  /*
   * Subscribe FIRST.
   */
  await subscribeToDevice(
    target,
  );

  const message = {
    id: crypto.randomUUID(),
    cmd: String(cmd),
    resp: String(resp),
  };

  const topic =
    `device/${target}/cmd`;

  console.log(
    '📤 MQTT publish:',
    topic,
    message,
  );

  /*
   * IMPORTANT:
   * Do NOT return success after publish.
   *
   * Return success ONLY after
   * SAME UUID comes back in CMDACK.
   */
  return new Promise(
    (resolve, reject) => {
      const timer =
        setTimeout(() => {
          if (
            pendingCommands.has(
              message.id,
            )
          ) {
            pendingCommands.delete(
              message.id,
            );

            console.log(
              '❌ ACK NOT RECEIVED',
            );

            console.log(
              '❌ Expected UUID:',
              message.id,
            );

            console.log(
              '❌ Device OFFLINE through MQTT:',
              target,
            );

            reject(
              new Error(
                'DEVICE_ACK_TIMEOUT',
              )
            );
          }
        }, ACK_TIMEOUT);

      /*
       * Store before publish so that
       * a very fast ACK cannot be missed.
       */
      pendingCommands.set(
        message.id,
        {
          resolve,
          reject,
          timer,
          message,
          deviceId: target,
        },
      );

      connection.publish(
        topic,
        JSON.stringify(
          message,
        ),
        {
          qos: 1,
          retain: false,
        },
        error => {
          if (error) {
            clearTimeout(
              timer,
            );

            pendingCommands.delete(
              message.id,
            );

            console.error(
              '❌ MQTT publish error:',
              error.message,
            );

            reject(error);

            return;
          }

          console.log(
            '✅ MQTT published',
          );

          console.log(
            '📤 UUID:',
            message.id,
          );

          console.log(
            `⏳ Waiting ${
              ACK_TIMEOUT / 1000
            } seconds for matching ACK...`,
          );
        },
      );
    }
  );
};


export const checkDeviceOnline =
  async deviceId => {
    try {
      const connection =
        getMqttClient();

      if (
        !connection.connected
      ) {
        console.log(
          '❌ MQTT broker not connected',
        );

        return false;
      }

      console.log(
        '🔎 Checking device using CMD 999:',
        deviceId,
      );

      /*
       * This only resolves when
       * SAME UUID is received.
       */
      const result =
        await sendCommand(
          deviceId,
          '999',
          '',
        );

      if (
        result?.success === true &&
        result?.ack?.id
      ) {
        console.log(
          '🟢 DEVICE WIFI ONLINE',
        );

        console.log(
          '🟢 ACK:',
          result.ack,
        );

        return true;
      }

      return false;

    } catch (error) {
      console.log(
        '🔴 DEVICE WIFI OFFLINE:',
        error.message,
      );

      return false;
    }
  };